<script setup lang="ts">
import { onKeyDown } from "@vueuse/core";
import AtomsIcon from "../icon/index.vue";

const props = withDefaults(
  defineProps<{
    open?: boolean;
    title?: string;
    subtitle?: string;
    closable?: boolean;
    contentCenter?: boolean;
    scrollContent?: boolean;
    size?: "default" | "medium" | "large";
    customContentPadding?: string;
    contentBackground?: "white" | "gray";
    customBackgroud?: string;
    zIndex?: number;
  }>(),
  {
    open: false,
    closable: true,
    size: "default",
    customContentPadding: "p-[24px_24px_16px] lg:p-[48px_32px_24px] ",
    contentBackground: "white",
    zIndex: 200,
  },
);
const emit = defineEmits<{
  (e: "update:open", value: boolean): void;
  (e: "force-close"): void;
}>();

const close = (force?: boolean) => {
  if (!props.closable) return;
  emit("update:open", false);
  if (force) {
    emit("force-close");
  }
};

onKeyDown("Escape", () => {
  if (!props.open) return;
  close();
});

defineExpose({
  close,
});
</script>

<template>
  <Transition name="modal-transition">
    <div class="modal" :style="{ zIndex }" v-if="open" :active="open" @click.self="close(false)">
      <aside
        :class="[
          'modal__content',
          scrollContent && '!pr-0',
          `modal__content--${size}`,
          !customBackgroud && `modal__content--bg-${contentBackground}`,
          customBackgroud,
          !$slots['actions'] && '!pb-0',
          customContentPadding,
        ]"
      >
        <button
          class="modal__content__close"
          type="button"
          @click="close(true)"
          v-if="closable"
        >
          <AtomsIcon name="x" :size="24" class="text-primary" />
        </button>
        <div
          class="modal__content__head"
          v-if="title || subtitle || $slots['subtitle']"
        >
          <span class="modal__content__head__title" v-if="title">{{
            title
          }}</span>
          <slot name="subtitle">
            <span class="modal__content__head__subtitle" v-if="subtitle">{{
              subtitle
            }}</span>
          </slot>
        </div>
        <div
          class="relative w-full"
          :class="[
            contentCenter && 'flex flex-col items-center justify-center',
            scrollContent && 'h-full max-h-full overflow-y-auto pr-8',
          ]"
        >
          <slot></slot>
        </div>
        <div
          class="modal__content__bottom"
          :class="[scrollContent && '!pr-[24px] lg:!pr-[32px]']"
        >
          <slot name="actions"></slot>
        </div>
      </aside>
    </div>
  </Transition>
</template>

<style lang="scss">
.modal-transition {
  &-leave-active {
    transition: opacity 0.3s ease 0s;
    @screen lg {
      .modal__content {
        transition: translate 0.5s ease;
        translate: 430px;
      }
    }
  }
  &-leave-to {
    opacity: 0;
  }
}

@keyframes modalContentSlide {
  0% {
    opacity: 0;
    translate: 500px;
  }
  100% {
    opacity: 1;
    translate: 0;
  }
}

.modal {
  @apply w-full h-[100vh] fixed left-0 top-0 z-[200] bg-[rgba(0,0,0,0.6)]
  flex justify-center lg:justify-end items-center lg:items-stretch animate-fadeIn;

  &__content {
    @apply w-[95%] lg:w-[480px] min-h-[40%] lg:min-h-[auto] lg:h-full overflow-auto rounded-l-[10px] rounded-r-[10px] lg:rounded-r-none
    relative flex flex-col items-stretch;

    &--medium {
      @apply lg:w-[760px];
    }

    &--large {
      @apply lg:w-[1100px];
    }

    &--bg-white {
      @apply bg-white;
    }

    &--bg-gray {
      @apply bg-[#E0E0E0];
    }

    @screen lg {
      animation: modalContentSlide 0.3s ease;
    }

    &__close {
      @apply h-[32px] w-[32px] flex items-center justify-center bg-transparent
      absolute top-2 lg:top-4 right-2 lg:right-4;
    }

    &__head {
      @apply flex flex-col mb-6 2xl:mb-8;
      &__title {
        @apply text-primary text-[24px] leading-5 font-bold pb-2;
      }

      &__subtitle {
        @apply text-primary text-[16px] leading-5 font-normal;
      }
    }

    &__bottom {
      @apply w-full mt-auto;
    }
  }
}
</style>
